import { ButtonType, MissionConfig } from '../types';
import { LiftCallRequest, LiftCallDirection, LiftCallRequestType, LiftStatus, SimulationState } from '../types/simulationState';
import '../styles/liftCallPanel.css';

export type PanelMode = 'floor' | 'lift';

export function createLiftCallPanel(
    panelMode: PanelMode,
    panelId: number | string, // floorNumber for floor mode, liftId for lift mode
    buttonType: ButtonType,
    simulationState: SimulationState,
    missionConfig: MissionConfig,
    onRequestsUpdated: () => void
): void {
    const existingOverlay = document.querySelector('.lift-call-panel-overlay');
    if (existingOverlay) {
        existingOverlay.remove();
    }

    const overlay = document.createElement('div');
    overlay.className = 'lift-call-panel-overlay';

    const modal = document.createElement('div');
    modal.className = 'lift-call-panel-modal';

    const header = document.createElement('div');
    header.className = 'lift-call-panel-header';

    const title = document.createElement('div');
    title.className = 'lift-call-panel-title';
    title.textContent = panelMode === 'floor' ? `Floor ${panelId} Call` : `Lift ${panelId} Control`;

    const closeBtn = document.createElement('button');
    closeBtn.className = 'lift-call-panel-close';
    closeBtn.textContent = '×';
    closeBtn.onclick = (): void => overlay.remove();

    header.append(title, closeBtn);
    modal.appendChild(header);

    const content = document.createElement('div');
    content.className = 'lift-call-panel-content';
    modal.appendChild(content);

    const refresh = (): void => {
        content.innerHTML = '';
        renderButtons(content, panelMode, panelId, buttonType, simulationState, missionConfig, (): void => {
            onRequestsUpdated();
            refresh();
        });
    };
    refresh();

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}

function getRequestsArray(panelMode: PanelMode, panelId: number | string, simulation: SimulationState): LiftCallRequest[] {
    if (panelMode === 'floor') {
        const floorState = simulation.building.floors[panelId as number];
        return floorState?.requests || [];
    } else {
        const liftState = simulation.building.lifts[panelId as string];
        return liftState?.requests || [];
    }
}

function renderButtons(
    container: HTMLElement,
    panelMode: PanelMode,
    panelId: number | string,
    buttonType: ButtonType,
    simulation: SimulationState,
    _missionConfig: MissionConfig,
    onUpdate: () => void
): void {
    const requests = getRequestsArray(panelMode, panelId, simulation);

    if (buttonType === 'SingleCall') {
        const isLit = isRequestActive(requests, 'single', null);
        const btn = createButton('●', 'Call', isLit, false, (): void => {
            toggleRequest(panelMode, panelId, simulation, 'single', null);
            onUpdate();
        });
        container.appendChild(btn);
    } else if (buttonType === 'UpDown') {
        const upBtn = createButton('▲', 'Up', isRequestActive(requests, 'direction', { requestedDirection: LiftCallDirection.UP }), false, (): void => {
            toggleRequest(panelMode, panelId, simulation, 'direction', { requestedDirection: LiftCallDirection.UP });
            onUpdate();
        });
        const downBtn = createButton('▼', 'Down', isRequestActive(requests, 'direction', { requestedDirection: LiftCallDirection.DOWN }), false, (): void => {
            toggleRequest(panelMode, panelId, simulation, 'direction', { requestedDirection: LiftCallDirection.DOWN });
            onUpdate();
        });

        const group = document.createElement('div');
        group.className = 'panel-up-down';
        group.append(upBtn, downBtn);
        container.appendChild(group);
    } else if (buttonType === 'NumberedFloors') {
        const group = document.createElement('div');
        group.className = 'panel-numbered-floors';

        const floorNumbers = Object.keys(simulation.building.floors).map(Number).sort((a, b) => a - b);

        floorNumbers.forEach(floor => {
            let disabled = false;
            if (panelMode === 'lift') {
                const liftState = simulation.building.lifts[panelId as string];
                if (liftState && liftState.status === LiftStatus.IDLE && liftState.currentFloor === floor) {
                    disabled = true;
                }
            }

            const btn = createButton(floor.toString(), `Floor ${floor}`, isRequestActive(requests, 'floor', { requestedFloor: floor }), disabled, (): void => {
                toggleRequest(panelMode, panelId, simulation, 'floor', { requestedFloor: floor });
                onUpdate();
            });
            group.appendChild(btn);
        });
        container.appendChild(group);
    }
}

function createButton(text: string, title: string, isLit: boolean, disabled: boolean, onClick: () => void): HTMLElement {
    const btn = document.createElement('button');
    btn.className = `lift-call-btn ${isLit ? 'lit' : ''}`;
    btn.textContent = text;
    btn.title = title;
    btn.disabled = disabled;
    if (!disabled) {
        btn.onclick = onClick;
    }
    return btn;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isRequestActive(requests: LiftCallRequest[], requestType: LiftCallRequestType, requestData: any): boolean {
    return requests.some(req =>
        req.requestType === requestType &&
        JSON.stringify(req.requestData) === JSON.stringify(requestData)
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toggleRequest(panelMode: PanelMode, panelId: number | string, simulation: SimulationState, requestType: LiftCallRequestType, requestData: any): void {
    let requests: LiftCallRequest[] | undefined;
    if (panelMode === 'floor') {
        requests = simulation.building.floors[panelId as number]?.requests;
    } else {
        requests = simulation.building.lifts[panelId as string]?.requests;
    }
    if (!requests) return;

    const existingIndex = requests.findIndex(req =>
        req.requestType === requestType &&
        JSON.stringify(req.requestData) === JSON.stringify(requestData)
    );

    if (existingIndex >= 0) {
        // requests.splice(existingIndex, 1);
        return; // already have a request
    }
    const newRequest: LiftCallRequest = {
        requestId: crypto.randomUUID(),
        requestType: requestType,
        requestData: requestData
    };
    requests.push(newRequest);
}
